<script lang="ts">
  import { onMount } from 'svelte';
  import { setupProjectAnimations, animateValue } from '$lib/utils/animations';
  import VideoPlayer from '$lib/components/VideoPlayer.svelte';
  import ProjectNav from '$lib/components/ProjectNav.svelte';
  import Footer from '$lib/components/Footer.svelte';
  import FeatureCell from '$lib/components/FeatureCell.svelte';
  import ProjectHeroFullWidth from '$lib/components/ProjectHeroFullWidth.svelte';
  import FullWidthImage from '$lib/components/FullWidthImage.svelte';
  import Quote from '$lib/components/Quote.svelte';
  import ChallengeSolution from '$lib/components/ChallengeSolution.svelte';
  import TwoImageRow from '$lib/components/TwoImageRow.svelte';
  import PortraitImage from '$lib/components/PortraitImage.svelte';
  let scrollY = 0;
  let translateY = 0;
  
  // Make the parallax effect reactive
  $: translateY = scrollY * 0.5;
</script>

<svelte:window bind:scrollY />

<div class="min-h-screen bg-white">
  <ProjectHeroFullWidth
    title="Blanq"
    client="Blanq Inc"
    industry="Fintech"
    clientLogo="/logos/Blanq_Logo.svg"
    product="Crypto wallet and physical card with distributed-key technology"
    heroMedia={{
      type: 'image',
      src: "https://www.nand.io/_next/image?url=/_next/static/media/blanq.a7178036.jpg&w=3840&q=80"
    }}
    metrics={[
      { value: 6, label: "Months Development" },
      { value: 3, label: "Platforms" },
      { value: 2, label: "Security Splits" }
    ]}
    roles={[
      "Strategy",
      "Visual Identity",
      "UX/UI Design"
    ]}
  />

  <div class="container mx-auto px-8 mt-16">
    <div class="space-y-2 mb-12 animate-on-view border-t border-b border-black/10 py-6" data-delay="1">
      <p class="large-text text-black/90">
        Materializing a start-up's vision into a sophisticated, user-friendly product.
      </p>
    </div>

    <ChallengeSolution
      challengeText="Transforming a fintech start-up's vision into a tangible product by designing and launching a minimum viable product within six months."
      solutionText="We created a crypto wallet mobile application, a physical banking card, and a website to attract early adopter buyers and validate the product concept. Appstore ready."
    />
  </div>



  <div class="container mx-auto px-8">
    <!-- Text Section -->
    <div class="mb-16 animate-on-view">
      <div class="grid grid-cols-12 gap-4">
        <h2 class="text-2xl font-light mt-8 mb-8 col-start-2 col-span-10">Grasping the technological complexity</h2>
      </div>
      <div class="grid grid-cols-12 gap-4">
        <p class="text-black/90 leading-relaxed col-start-2 col-span-10">
          The Blanq crypto wallet aims to tackle two of the most common problems associated with non-custodial wallets:

          Increased responsibility placed on users to safeguard their passwords
          The technical complexity of setting up and recovering passwords in case of loss or misplacement.
          The key feature and unique selling point of Blanq is its into multiple individual shares in the hardware, comprising a mobile app and a physical card that runs on Blanq's sophisticated security algorithm.
        </p>
      </div>

    </div>
  </div>

  <!-- Video Section 2 -->
  <div class="w-full mt-16 mb-16">
    <div class="w-full">
      <VideoPlayer
        src="https://vz-6b226b53-3c8.b-cdn.net/0696d5d1-4415-4dc6-b046-b2626e106951/playlist.m3u8"
        autoplay={true}
        loop={true}
        controls={false}
        muted={true}
        className="w-full h-full object-cover"
      />
    </div>
  </div>

  <div class="container mx-auto px-8">
    <!-- Text Section -->
    <div class="mb-16 animate-on-view">
      <div class="grid grid-cols-12 gap-4">
        <p class="text-black/90 leading-relaxed col-start-2 col-span-10">
          In Blanq's pioneering effort to enhance security, we designed a unique physical card that holds one split of the cryptographic secret, marking the product as the first-ever distributed-key hardware wallet. The design features materials closely tied to security: the logo printed in optically variable ink, commonly found on bank notes, and a translucent back that makes the antenna visible and highlights the chip.
        </p>
      </div>
    </div>
  </div>

  

  <!-- Full Width Image Section -->
  <FullWidthImage
    src="https://www.nand.io/_next/image?url=/_next/static/media/card-back-front.28169ceb.jpg&w=3840&q=80"
    alt="Blanq physical card design"
    caption="The physical card design featuring optically variable ink and a translucent back revealing the antenna and chip."
  />

  <!-- Image Gallery -->
  <section class="animate-on-view">
    <div class="grid grid-cols-12 gap-4">
      <h2 class="text-2xl font-light mt-8 mb-8 col-start-2 col-span-10">The physical card design</h2>
    </div>
    <div class="flex flex-col md:flex-row gap-8 justify-center items-center mt-12">
      
      <figure class="max-w-xs w-full">
        <img
          src="/projects/blanq/blanq-card-draft.png"
          alt="Blanq card front with blue arcs"
          class="w-full h-auto object-contain"
          style="max-width: 400px;"
        />
        <figcaption class="text-sm text-gray-600 italic mt-2 text-center">Blanq card front process render</figcaption>
      </figure>
      <figure class="max-w-xs w-full">
        <img
          src="/projects/blanq/Credit Card Mockup.jpg"
          alt="Blanq black card in hand"
          class="w-full h-auto object-contain"
          style="max-width: 400px;"
        />
        <figcaption class="text-sm text-gray-600 italic mt-2 text-center">Blanq black card in hand process photo</figcaption>
      </figure>
    </div>
  </section>

  <div class="container mx-auto px-8">
    <!-- Text Section -->
    <div class="mb-16 mt-16 animate-on-view">
      <div class="grid grid-cols-12 gap-4">
        <p class="text-black/90 leading-relaxed col-start-2 col-span-10">
          The card's design is a reflection of Blanq's commitment to security and user experience. The optically variable ink logo and translucent back not only make the card visually appealing but also serve as a visual reminder of the cryptographic secrets it holds.
        </p>
      </div>
    </div>
  </div>



  <!-- Key Features Section -->
  <div class="container mx-auto px-8">
    <h2 class="text-2xl font-light mt-8 mb-8">Key Features</h2>
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <FeatureCell
        icon="shield"
        title="Distributed Security"
        description="Split cryptographic secrets across hardware components"
      />
      
      <FeatureCell
        icon="smartphone"
        title="Mobile App"
        description="User-friendly interface for iOS and Android"
      />

      <FeatureCell
        icon="credit-card"
        title="Physical Card"
        description="High-security card with optically variable ink"
      />

      <FeatureCell
        icon="refresh-ccw"
        title="Easy Recovery"
        description="Simplified password recovery process"
      />
    </div>
  </div>

  <!-- Additional Feature Cell -->
  <Quote 
    text="Your secret is nowhere — it's Blanq. The first-ever distributed-key hardware wallet, setting new standards in crypto security."
  />

  <FullWidthImage
  src="/projects/blanq/Blanq-Leaflet.png"
  alt="Blanq packaging"
  caption="Blanq packaging"
/>

      <!-- Two-column statement block -->
      <div class="container mx-auto px-8">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8 items-center my-12">
        <div>
          <h2>Transforming technical innovation into an accessible experience</h2>
        </div>
        <div>
          <p class="text-lg text-gray-800">I understand the critical importance of guiding users through complex processes by revealing the right information at the right time. By carefully anticipating user needs and expectations, I ensure that the designs simplify the experience, making it accessible even to those unfamiliar with the intricacies of cryptocurrency transactions.</p>
        </div>
      </div>
    </div>

    <PortraitImage
    src="/projects/blanq/IMG_9842 copy.PNG"
    alt="Blanq Portrait"
    bgColor="#f5f5f5"
    animatedText="Blanq crypto wallet in the apple store"
  />

  <!-- Project Navigation -->
  <ProjectNav 
    prevProject={{ title: "Made to Measure", url: "/projects/made-to-measure" }}
    nextProject={{ title: "FactSnap", url: "/projects/fact-snap" }}
  />

  <Footer />
</div>
